import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { getSiteName, useContent } from "../lib/content";
import { toast } from "sonner";
import SiteContentPanel from "../components/SiteContentPanel";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { content } = useContent();
  const siteName = getSiteName(content);
  const [tab, setTab] = useState("projects");
  const [sections, setSections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Project form
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);

  // Section form
  const [editingSection, setEditingSection] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [secRes, projRes] = await Promise.all([
        api.get("/sections"),
        api.get("/projects"),
      ]);
      setSections(secRes.data);
      setProjects(projRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("archdigital_admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("archdigital_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    refresh();
  }, [navigate, refresh]);

  const logout = () => {
    localStorage.removeItem("archdigital_admin_token");
    navigate("/admin");
  };

  const deleteProject = async (project) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      const key = project?.id || project?.slug;
      await api.post(`/projects/${encodeURIComponent(key)}/delete`);
      setProjects((current) =>
        current.filter((p) => p.id !== project?.id && p.slug !== project?.slug)
      );
      toast.success("Project deleted.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete.");
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm("Delete this section? Its projects will become uncategorized.")) return;
    try {
      await api.post(`/sections/${id}/delete`);
      setSections((current) => current.filter((s) => s.id !== id));
      setProjects((current) =>
        current.map((p) => (p.section_id === id ? { ...p, section_id: null } : p))
      );
      toast.success("Section deleted.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete.");
    }
  };

  const reorderProjects = async (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= projects.length) return;
    const next = [...projects];
    [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
    setProjects(next);
    try {
      await api.post("/projects/reorder", {
        ids: next.map((p) => p.id || p.slug),
      });
      toast.success("Project order updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update order.");
      refresh();
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-ink-900/80 border-b border-bone-50/5">
        <div className="container flex items-center justify-between py-5">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-display text-xl tracking-tightest">
              {siteName}
            </Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">
              / Studio Console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-bone-200/60 hover:text-bone-50"
              data-testid="view-site-link"
            >
              View site ↗
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="font-mono text-[11px] uppercase tracking-[0.25em] border border-bone-50/20 px-3 py-2 hover:border-ember hover:text-ember transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container py-12">
        <div className="flex items-center gap-2 mb-10" data-testid="admin-tabs">
          <button
            onClick={() => setTab("projects")}
            data-testid="tab-projects"
            className={`font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2 border transition-all ${
              tab === "projects"
                ? "bg-bone-50 text-ink-900 border-bone-50"
                : "border-bone-50/20 text-bone-200/60 hover:border-bone-50/60"
            }`}
          >
            Projects / {projects.length}
          </button>
          <button
            onClick={() => setTab("sections")}
            data-testid="tab-sections"
            className={`font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2 border transition-all ${
              tab === "sections"
                ? "bg-bone-50 text-ink-900 border-bone-50"
                : "border-bone-50/20 text-bone-200/60 hover:border-bone-50/60"
            }`}
          >
            Sections / {sections.length}
          </button>
          <button
            onClick={() => setTab("content")}
            data-testid="tab-content"
            className={`font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2 border transition-all ${
              tab === "content"
                ? "bg-bone-50 text-ink-900 border-bone-50"
                : "border-bone-50/20 text-bone-200/60 hover:border-bone-50/60"
            }`}
          >
            Site content
          </button>
        </div>

        {tab === "projects" && (
          <ProjectsPanel
            projects={projects}
            sections={sections}
            loading={loading}
            onAdd={() => {
              setEditingProject(null);
              setShowProjectModal(true);
            }}
            onEdit={(p) => {
              setEditingProject(p);
              setShowProjectModal(true);
            }}
            onDelete={deleteProject}
            onMove={reorderProjects}
          />
        )}

        {tab === "sections" && (
          <SectionsPanel
            sections={sections}
            projects={projects}
            loading={loading}
            onAdd={() => {
              setEditingSection(null);
              setShowSectionModal(true);
            }}
            onEdit={(s) => {
              setEditingSection(s);
              setShowSectionModal(true);
            }}
            onDelete={deleteSection}
          />
        )}

        {tab === "content" && <SiteContentPanel />}
      </div>

      {showProjectModal && (
        <ProjectModal
          project={editingProject}
          sections={sections}
          onClose={() => setShowProjectModal(false)}
          onSaved={() => {
            setShowProjectModal(false);
            refresh();
          }}
        />
      )}

      {showSectionModal && (
        <SectionModal
          section={editingSection}
          onClose={() => setShowSectionModal(false)}
          onSaved={() => {
            setShowSectionModal(false);
            refresh();
          }}
        />
      )}
    </main>
  );
}

function ProjectsPanel({ projects, sections, loading, onAdd, onEdit, onDelete, onMove }) {
  const sectionName = (id) => sections.find((s) => s.id === id)?.name || "—";
  return (
    <div data-testid="projects-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-display text-4xl tracking-tightest">Projects</h2>
          <p className="text-bone-200/50 text-sm mt-2">
            Every published render, essay, and exhibition.
          </p>
        </div>
        <button
          onClick={onAdd}
          data-testid="add-project-btn"
          className="font-mono text-xs uppercase tracking-[0.3em] bg-ember text-ink-900 px-5 py-3 hover:bg-ember-light transition-colors"
        >
          + New Project
        </button>
      </div>

      {loading ? (
        <div className="text-bone-200/50 font-mono text-xs uppercase py-20 text-center">
          Loading…
        </div>
      ) : projects.length === 0 ? (
        <div className="border border-dashed border-bone-50/10 p-20 text-center">
          <p className="font-display text-3xl mb-3">No projects yet.</p>
          <p className="text-bone-200/50 text-sm">
            Create your first project to see it appear on the site.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {projects.map((p, idx) => (
            <div
              key={p.id}
              data-testid={`project-row-${p.slug}`}
              className="flex items-center gap-6 p-4 border border-bone-50/5 hover:border-bone-50/20 transition-colors group"
            >
              <div className="h-20 w-28 bg-ink-700 flex-shrink-0 overflow-hidden">
                {p.cover_url ? (
                  <img
                    src={p.cover_url}
                    alt={p.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-bone-200/30 font-mono text-[10px]">
                    NO IMG
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl truncate">{p.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/50 mt-1">
                  {sectionName(p.section_id)} · {p.year || "—"} · /{p.slug}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onMove(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`move-project-up-${p.slug}`}
                  aria-label={`Move ${p.title} up`}
                  className="font-mono text-[10px] uppercase border border-bone-50/20 px-2 py-2 hover:border-ember hover:text-ember transition-colors disabled:opacity-30 disabled:hover:border-bone-50/20 disabled:hover:text-inherit"
                >
                  ↑
                </button>
                <button
                  onClick={() => onMove(idx, 1)}
                  disabled={idx === projects.length - 1}
                  data-testid={`move-project-down-${p.slug}`}
                  aria-label={`Move ${p.title} down`}
                  className="font-mono text-[10px] uppercase border border-bone-50/20 px-2 py-2 hover:border-ember hover:text-ember transition-colors disabled:opacity-30 disabled:hover:border-bone-50/20 disabled:hover:text-inherit"
                >
                  ↓
                </button>
                <button
                  onClick={() => onEdit(p)}
                  data-testid={`edit-project-${p.slug}`}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 px-3 py-2 hover:border-ember hover:text-ember transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(p)}
                  data-testid={`delete-project-${p.slug}`}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 px-3 py-2 hover:border-red-500 hover:text-red-500 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionsPanel({ sections, projects, loading, onAdd, onEdit, onDelete }) {
  return (
    <div data-testid="sections-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-display text-4xl tracking-tightest">Sections</h2>
          <p className="text-bone-200/50 text-sm mt-2">
            Group projects into curated collections.
          </p>
        </div>
        <button
          onClick={onAdd}
          data-testid="add-section-btn"
          className="font-mono text-xs uppercase tracking-[0.3em] bg-ember text-ink-900 px-5 py-3 hover:bg-ember-light transition-colors"
        >
          + New Section
        </button>
      </div>

      {loading ? (
        <div className="text-bone-200/50 font-mono text-xs uppercase py-20 text-center">
          Loading…
        </div>
      ) : sections.length === 0 ? (
        <div className="border border-dashed border-bone-50/10 p-20 text-center">
          <p className="font-display text-3xl mb-3">No sections yet.</p>
          <p className="text-bone-200/50 text-sm">
            Create a section like "Residential" or "Cultural".
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {sections.map((s) => {
            const count = projects.filter((p) => p.section_id === s.id).length;
            return (
              <div
                key={s.id}
                data-testid={`section-row-${s.slug}`}
                className="p-6 border border-bone-50/5 hover:border-bone-50/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-display text-2xl">{s.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/50 mt-1">
                      /{s.slug} · {count} project{count === 1 ? "" : "s"}
                    </div>
                    {s.description && (
                      <p className="text-bone-200/60 text-sm mt-3 leading-relaxed">
                        {s.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => onEdit(s)}
                    data-testid={`edit-section-${s.slug}`}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 px-3 py-2 hover:border-ember hover:text-ember transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(s.id)}
                    data-testid={`delete-section-${s.slug}`}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 px-3 py-2 hover:border-red-500 hover:text-red-500 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectModal({ project, sections, onClose, onSaved }) {
  const [title, setTitle] = useState(project?.title || "");
  const [slug, setSlug] = useState(project?.slug || "");
  const [description, setDescription] = useState(project?.description || "");
  const [location, setLocation] = useState(project?.location || "");
  const [year, setYear] = useState(project?.year || "");
  const [sectionId, setSectionId] = useState(project?.section_id || "");
  const [coverUrl, setCoverUrl] = useState(project?.cover_url || "");
  const [images, setImages] = useState(project?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpload = async (file, target) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url;
      if (target === "cover") {
        setCoverUrl(url);
      } else {
        setImages([...images, { id: res.data.id, url, caption: "" }]);
      }
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title,
      slug: slug || slugify(title),
      description,
      location,
      year,
      section_id: sectionId || null,
      cover_url: coverUrl,
      images,
    };
    try {
      if (project?.id) {
        await api.put(`/projects/${project.id}`, payload);
      } else {
        await api.post("/projects", payload);
      }
      toast.success(project?.id ? "Project updated." : "Project created.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const removeImage = (idx) => setImages(images.filter((_, i) => i !== idx));
  const updateCaption = (idx, caption) => {
    const next = [...images];
    next[idx] = { ...next[idx], caption };
    setImages(next);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="project-modal"
    >
      <div className="bg-ink-800 border border-bone-50/10 w-full max-w-3xl my-auto">
        <div className="flex items-center justify-between p-6 border-b border-bone-50/5">
          <h3 className="font-display text-2xl">
            {project?.id ? "Edit project" : "New project"}
          </h3>
          <button
            onClick={onClose}
            data-testid="close-project-modal"
            className="text-bone-200/60 hover:text-bone-50"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-5">
          <Field label="Title" required>
            <input
              data-testid="project-title-input"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!project?.id) setSlug(slugify(e.target.value));
              }}
              required
              className={inputCls}
            />
          </Field>
          <Field label="URL slug">
            <input
              data-testid="project-slug-input"
              value={slug}
              onChange={(e) => setSlug(slugify(e.target.value))}
              className={inputCls}
              placeholder="auto-generated-from-title"
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Section">
              <select
                data-testid="project-section-select"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                className={inputCls}
              >
                <option value="">— None —</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Year">
              <input
                data-testid="project-year-input"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className={inputCls}
                placeholder="2026"
              />
            </Field>
          </div>
          <Field label="Location">
            <input
              data-testid="project-location-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={inputCls}
              placeholder="Berlin, DE"
            />
          </Field>
          <Field label="Description">
            <textarea
              data-testid="project-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className={inputCls}
            />
          </Field>

          <Field label="Cover image">
            <div className="flex items-center gap-4">
              {coverUrl ? (
                <div className="relative">
                  <img
                    src={coverUrl}
                    alt="cover"
                    className="h-24 w-36 object-cover border border-bone-50/10"
                  />
                  <button
                    type="button"
                    onClick={() => setCoverUrl("")}
                    className="absolute -top-2 -right-2 h-6 w-6 bg-ink-900 border border-bone-50/20 text-bone-50 text-xs"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <label
                  data-testid="upload-cover-btn"
                  className="cursor-pointer border border-dashed border-bone-50/20 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/60 hover:border-ember hover:text-ember"
                >
                  {uploading ? "Uploading…" : "+ Upload cover"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0], "cover")}
                  />
                </label>
              )}
            </div>
          </Field>

          <Field label="Gallery images">
            <div className="space-y-3">
              {images.map((img, i) => (
                <div key={i} className="flex items-center gap-3">
                  <img
                    src={img.url}
                    alt=""
                    className="h-14 w-20 object-cover border border-bone-50/10"
                  />
                  <input
                    data-testid={`project-image-caption-${i}`}
                    value={img.caption || ""}
                    onChange={(e) => updateCaption(i, e.target.value)}
                    placeholder="Caption (optional)"
                    className={inputCls + " flex-1"}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    data-testid={`remove-image-${i}`}
                    className="font-mono text-[10px] uppercase px-2 py-2 border border-bone-50/20 hover:border-red-500 hover:text-red-500"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <label
                data-testid="upload-gallery-image-btn"
                className="inline-block cursor-pointer border border-dashed border-bone-50/20 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/60 hover:border-ember hover:text-ember"
              >
                {uploading ? "Uploading…" : "+ Add gallery image"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0], "gallery")}
                />
              </label>
            </div>
          </Field>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-bone-50/5">
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-xs uppercase tracking-[0.25em] border border-bone-50/20 px-5 py-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-project-btn"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-ember text-ink-900 px-5 py-3 hover:bg-ember-light disabled:opacity-50"
            >
              {saving ? "Saving…" : project?.id ? "Save changes" : "Publish project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SectionModal({ section, onClose, onSaved }) {
  const [name, setName] = useState(section?.name || "");
  const [slug, setSlug] = useState(section?.slug || "");
  const [description, setDescription] = useState(section?.description || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name, slug: slug || slugify(name), description };
    try {
      if (section?.id) {
        await api.put(`/sections/${section.id}`, payload);
      } else {
        await api.post("/sections", payload);
      }
      toast.success(section?.id ? "Section updated." : "Section created.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="section-modal"
    >
      <div className="bg-ink-800 border border-bone-50/10 w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-bone-50/5">
          <h3 className="font-display text-2xl">
            {section?.id ? "Edit section" : "New section"}
          </h3>
          <button
            onClick={onClose}
            data-testid="close-section-modal"
            className="text-bone-200/60 hover:text-bone-50"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-5">
          <Field label="Name" required>
            <input
              data-testid="section-name-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!section?.id) setSlug(slugify(e.target.value));
              }}
              required
              className={inputCls}
              placeholder="Residential"
            />
          </Field>
          <Field label="URL slug">
            <input
              data-testid="section-slug-input"
              value={slug}
              onChange={(e) => setSlug(slugify(e.target.value))}
              className={inputCls}
            />
          </Field>
          <Field label="Description">
            <textarea
              data-testid="section-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={inputCls}
            />
          </Field>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-bone-50/5">
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-xs uppercase tracking-[0.25em] border border-bone-50/20 px-5 py-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-section-btn"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-ember text-ink-900 px-5 py-3 hover:bg-ember-light disabled:opacity-50"
            >
              {saving ? "Saving…" : section?.id ? "Save changes" : "Create section"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-bone-200/50 mb-2">
        {label} {required && <span className="text-ember">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-ink-900 border border-bone-50/10 px-4 py-3 focus:outline-none focus:border-ember transition-colors text-bone-50";

function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
